import { DeleteOutlined, ExclamationCircleFilled, PlusOutlined } from '@ant-design/icons';
import { Col, Row, Space, Typography, Spin, Button, Table, message, ModalProps, Modal } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Outlet, useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../../api/useAPIMutation';
import useAPIQuery from '../../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../../api/useInfiniteAPIQuery';
import ErrorResult from '../../../components/ErrorResult';
import usePrefetchPages from '../../../hooks/usePrefetchPages';
import useTypedNavigate from '../../../hooks/useTypedNavigate';
import useTypedParams from '../../../hooks/useTypedParams';
import { withRouteChange } from '../../../services/APM';
import { OAuth2ClientCollection } from '../../../types';

interface ConfirmClientDeleteModalProps extends ModalProps {
  clientName: string;
  onSuccess?: () => void | Promise<unknown>;
}

const ConfirmClientDeleteModal = ({ clientName, ...props }: ConfirmClientDeleteModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      okButtonProps={{
        htmlType: 'button',
      }}
      okText={t('Sim, deletar client')}
      title={
        <Typography.Text strong>
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 20 }} />
          <Trans>Atenção!</Trans>
        </Typography.Text>
      }
      {...props}
    >
      <Typography.Text>
        <Trans>Ao confirmar, o Client</Trans> <Typography.Text strong>{clientName}</Typography.Text>{' '}
        <Trans>
          será excluído permanentemente e não poderá ser recuperado. A chave pública e Client ID
          cadastrados não existirão mais e a integração através desses dados deixará de funcionar.
          Tem certeza que deseja continuar?
        </Trans>
      </Typography.Text>
    </Modal>
  );
};

const StaffExternalAPI = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const [clientID, setClientID] = useState<OAuth2ClientCollection['id']>(0);

  const queryClient = useQueryClient();

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();

  const oauthClients = useAPIQuery([
    '/oauth-clients/',
    {
      params: {
        page: parseInt(searchParams.get('page') || '1', 10),
        page_size: parseInt(searchParams.get('pageSize') || '10', 10),
      },
    },
  ]);

  const usersPages = useInfiniteAPIQuery([
    '/staff-users/',
    {
      params: {
        is_active: true,
      },
    },
  ]);
  const users = usePrefetchPages(usersPages, (data) => data.results);

  const deleteOAuthClientMutation = useAPIMutation(
    async (oauthClientID: OAuth2ClientCollection['id'], api) => {
      await api.delete(`/oauth-clients/${oauthClientID}/`);
    },
    {
      onSuccess: () => {
        message.success(t('Client removido com sucesso!'));
      },
      onSettled: () => {
        queryClient.invalidateQueries('/oauth-clients/');
      },
    }
  );

  return (
    <ErrorResult error={oauthClients.error}>
      <ConfirmClientDeleteModal
        key={`ConfirmClientDeleteModal-${clientID}`}
        open={!!clientID}
        onOk={() => {
          setClientID(0);
          deleteOAuthClientMutation.mutate(clientID);
        }}
        onCancel={() => setClientID(0)}
        onSuccess={() => {
          setClientID(0);
        }}
        clientName={
          oauthClients.data?.results.find((oauthClient) => oauthClient.id === clientID)
            ?.client_name || ''
        }
        destroyOnClose
      />
      <Helmet>
        <title>
          {t('API | Integrações Externas | Área do CS')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Outlet />
      <Typography.Paragraph>
        <Trans>
          Configurações para acesso à API externa do tenant. Abaixo, adicione Clients utilizando a
          chave pública que o cliente fornecer e um nome. Retorne o Client ID para o cliente
          conseguir prosseguir com a integração.
        </Trans>
      </Typography.Paragraph>
      <Space direction="vertical" size="middle">
        <Spin spinning={oauthClients.isLoading}>
          <Row style={{ paddingBottom: '2%' }}>
            <Col flex="auto" />
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  navigate({
                    pathname: '/:tenant/settings/cs-staff/external-integrations/api/create/',
                    params: { tenant },
                  })
                }
              >
                {' '}
                <Trans>Novo Client</Trans>
              </Button>
            </Col>
          </Row>
          <Table
            rowKey="id"
            dataSource={oauthClients.data?.results}
            loading={oauthClients.isFetching}
            pagination={{
              showTotal: (total) => {
                return `Total: ${total} clients`;
              },
              total: oauthClients.data?.count,
              onChange: (page, pageSize) => {
                searchParams.set('page', page.toString());
                searchParams.set('pageSize', pageSize.toString());
                setSearchParams(searchParams);
              },
              current: parseInt(searchParams.get('page') || '1', 10),
              pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
            }}
          >
            <Table.Column
              title={t('Client Name')}
              render={(record: OAuth2ClientCollection) => (
                <Typography.Text>{record.client_name}</Typography.Text>
              )}
            />
            <Table.Column
              title={t('Client ID')}
              render={(record: OAuth2ClientCollection) => (
                <Typography.Text>{record.client_id}</Typography.Text>
              )}
            />
            <Table.Column
              title={t('Criado por')}
              render={(record: OAuth2ClientCollection) => (
                <Typography.Text>
                  {users.find((user) => user.id === record.user)?.full_name}
                </Typography.Text>
              )}
            />
            <Table.Column
              render={(record: OAuth2ClientCollection) => (
                <Button
                  danger
                  onClick={() => {
                    setClientID(record.id);
                  }}
                >
                  <DeleteOutlined />
                </Button>
              )}
            />
          </Table>
        </Spin>
      </Space>
    </ErrorResult>
  );
};

export default withRouteChange()(StaffExternalAPI);
